import React, { useState, useRef, useEffect } from "react"
import { Clock, Pencil, Trash2, User, CheckCircle2, Circle, RotateCcw } from "lucide-react"
import { cn } from "src/utils/cn"
import { Button } from "src/components/ui/button"
import { CHANNEL_BY_VALUE, FOLLOW_UP_OUTCOMES, OUTCOME_BY_VALUE } from "../constants"

const PRIORITY_STYLES = {
  low:    { dot: "bg-slate-400",  label: "Low",    text: "text-slate-500" },
  medium: { dot: "bg-blue-500",   label: "Med",    text: "text-blue-600" },
  high:   { dot: "bg-amber-500",  label: "High",   text: "text-amber-600" },
  urgent: { dot: "bg-red-500",    label: "Urgent", text: "text-red-600" },
}

function formatDue(due_at) {
  if (!due_at) return null
  const d = new Date(due_at)
  const now = new Date()
  const isToday = d.toDateString() === now.toDateString()
  const isTomorrow = d.toDateString() === new Date(now.getTime() + 86400000).toDateString()
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  if (isToday) return `Today ${time}`
  if (isTomorrow) return `Tomorrow ${time}`
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " + time
}

// Inline outcome picker shown when logging a completed follow-up.
function OutcomeMenu({ onPick, onClose }) {
  const ref = useRef(null)
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [onClose])

  return (
    <div ref={ref} className="absolute right-0 top-full z-50 mt-1 w-44 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
      <p className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">Log outcome</p>
      {FOLLOW_UP_OUTCOMES.map((o) => (
        <Button
          key={o.value}
          type="button"
          variant="ghost"
          onClick={() => onPick(o.value)}
          className="h-auto w-full justify-start gap-2 rounded-md px-2 py-1.5 text-left text-[13px] font-normal text-slate-700 hover:bg-slate-50"
        >
          <span className={cn("h-2 w-2 rounded-full", o.cls.split(" ")[0])} />
          {o.label}
        </Button>
      ))}
    </div>
  )
}

export function FollowUpCard({ followUp, onComplete, onReopen, onEdit, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const p = PRIORITY_STYLES[followUp.priority] || PRIORITY_STYLES.medium
  const channel = CHANNEL_BY_VALUE[followUp.channel] || CHANNEL_BY_VALUE.other
  const ChannelIcon = channel.Icon
  const isDone = followUp.status === "completed"
  const isOverdue = followUp.overdue && !isDone
  const outcome = followUp.outcome ? OUTCOME_BY_VALUE[followUp.outcome] : null

  return (
    <div className={cn(
      "relative flex gap-3 rounded-xl border px-4 py-3 transition-all",
      isDone ? "border-slate-100 bg-slate-50/60" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm",
      isOverdue && "border-red-200 bg-red-50/40"
    )}>
      {/* Channel avatar doubles as complete toggle */}
      <div className="relative mt-0.5 flex-shrink-0">
        {isDone ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onReopen?.(followUp.id)}
            className="h-7 w-7 p-0 text-emerald-500 hover:bg-transparent hover:text-slate-400"
            title="Reopen follow-up"
          >
            <CheckCircle2 className="h-7 w-7" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMenuOpen((v) => !v)}
            className={cn("group h-7 w-7 items-center justify-center rounded-full", channel.cls)}
            title="Log outcome & complete"
          >
            <ChannelIcon className="h-3.5 w-3.5 group-hover:hidden" />
            <Circle className="hidden h-4 w-4 group-hover:block" />
          </Button>
        )}
        {menuOpen && (
          <OutcomeMenu
            onClose={() => setMenuOpen(false)}
            onPick={(o) => { setMenuOpen(false); onComplete?.(followUp.id, o) }}
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={cn("text-sm font-medium leading-snug", isDone ? "line-through text-slate-400" : "text-slate-800")}>
            {followUp.title}
          </p>
          <div className="flex flex-shrink-0 items-center gap-1">
            {isDone && onReopen && (
              <Button variant="ghost" size="icon" onClick={() => onReopen(followUp.id)} className="h-6 w-6 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Reopen">
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            )}
            {onEdit && (
              <Button variant="ghost" size="icon" onClick={() => onEdit(followUp)} className="h-6 w-6 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Edit">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDelete && (
              <Button variant="ghost" size="icon" onClick={() => onDelete(followUp.id)} className="h-6 w-6 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500" title="Delete">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {followUp.description && (
          <p className="mt-0.5 text-[12px] text-slate-500 line-clamp-2">{followUp.description}</p>
        )}

        <div className="mt-1.5 flex flex-wrap items-center gap-2.5">
          {/* Channel */}
          <span className="text-[11px] font-medium text-slate-500">{channel.label}</span>

          {/* Priority */}
          <span className="flex items-center gap-1 text-[11px] font-medium">
            <span className={cn("h-1.5 w-1.5 rounded-full", p.dot)} />
            <span className={p.text}>{p.label}</span>
          </span>

          {/* Due */}
          {followUp.due_at && (
            <span className={cn("flex items-center gap-1 text-[11px]", isOverdue ? "text-red-600 font-medium" : "text-slate-400")}>
              <Clock className="h-3 w-3" />
              {formatDue(followUp.due_at)}
            </span>
          )}

          {/* Assignee */}
          {followUp.assignee && (
            <span className="flex items-center gap-1 text-[11px] text-slate-400">
              <User className="h-3 w-3" />
              {followUp.assignee.name}
            </span>
          )}

          {/* Outcome badge (when completed) */}
          {isDone && outcome && (
            <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-medium", outcome.cls)}>
              {outcome.label}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
